import { useParams } from 'react-router-dom'
import type * as Api from 'src/api'

/** credit_type values used by POST /choices/history to pick the numeric/doctoral/pass-fail body variant. */
export const CREDIT_TYPE_RECOGNIZED = 2
export const CREDIT_TYPE_PRE_ARGUS = 3
export const CREDIT_TYPE_DOCTORAL = 4
export const CREDIT_TYPE_PASS_FAIL = 5

/** Choice `status` values: 1 passed, 2 in progress (active semester), 3 failed. */
export const CHOICE_STATUS_PASSED = 1

/** `programId` for PATCH /program-assignment that moves a choice into free credits. */
export const FREE_CREDITS_PROGRAM_ID = -1

/** `programId` that clears a manual assignment, returning a general-module course to the general module. */
export const GENERAL_MODULE_PROGRAM_ID = 0

export interface StudentChoiceRouteParams {
  readonly uid: string
  readonly profileId: string
}

export function useStudentChoiceParams(): StudentChoiceRouteParams {
  const { uid, profileId } = useParams()
  return { uid: uid ?? '', profileId: profileId ?? '' }
}

/** Tailwind classes shared by the inline row editors on the choices pages. */
export const CHOICE_INPUT_CLASS_NAME =
  'w-full rounded-[5px] border border-borderGrey bg-card px-2 py-1 text-sm text-primaryTextColor outline-none'

/** PATCH body fields the row editor can offer, in display order. */
export const EDITABLE_FIELDS = ['couName', 'couCode', 'couGeneral', 'couCredits', 'score'] as const
export type EditableField = (typeof EDITABLE_FIELDS)[number]

/** Only a recognized credit takes a hand-edited course credit amount and score, in any semester. */
const RECOGNIZED_CREDIT_FIELDS: readonly EditableField[] = ['couCredits', 'score']

/**
 * The API says per row which fields it will accept — a passed course usually allows only
 * `couCode`. Sending anything outside that set is rejected with 422, so the editor offers
 * exactly what the row permits, and falls back to all of them when the API stays silent.
 */
export function editableFieldsOf(choice: Api.AdministratorChoiceRow): readonly EditableField[] {
  const allowed = choice.editableFields
  const isRecognized = choice.creditType === CREDIT_TYPE_RECOGNIZED
  return EDITABLE_FIELDS.filter((field) =>
    RECOGNIZED_CREDIT_FIELDS.includes(field) ? isRecognized : allowed == null || allowed.includes(field)
  )
}

export interface ChoiceRowValues {
  readonly couName: string
  readonly couCode: string
  readonly couGeneral: boolean
  readonly couCredits: string
  /** Set once course credits are edited; from then on gained credit follows them, even back to the old value. */
  readonly creditsSynced: boolean
  readonly score: string
}

/** Formik values of an inline choices editor: one entry per row, keyed by choice uid, so any number can be open. */
// A type alias, not an interface: Formik's `FormikValues` needs the implicit index signature.
export type ChoicesFormValues = {
  readonly rows: Readonly<Record<string, ChoiceRowValues>>
}

export function choiceRowValuesOf(choice: Api.AdministratorChoiceRow): ChoiceRowValues {
  return {
    couName: choice.courseName,
    couCode: choice.courseCode,
    couGeneral: choice.couGeneral === 1,
    couCredits: String(choice.courseCredits),
    creditsSynced: false,
    score: String(choice.score),
  }
}

/** Formik hands number inputs back as numbers, so values are compared and parsed through `String`. */
function numberOf(value: string): number | null {
  const trimmed = String(value).trim()
  return trimmed === '' ? null : Number(trimmed)
}

/**
 * Gained credit is never edited by hand: it keeps its stored value until course credits are
 * edited, then follows them, live while editing and in the PATCH body.
 */
export function gainedCreditsOf(choice: Api.AdministratorChoiceRow, row: ChoiceRowValues | undefined): number {
  if (row == null || !row.creditsSynced || !editableFieldsOf(choice).includes('couCredits')) return choice.credits
  return numberOf(row.couCredits) ?? choice.credits
}

export function choicePatchBodyOf(
  choice: Api.AdministratorChoiceRow,
  row: ChoiceRowValues,
  allowed: readonly EditableField[]
): Omit<Api.patchAdministratorStudentChoiceParams['body'], 'targetSemesterId'> {
  const couCredits = numberOf(row.couCredits)
  const score = numberOf(row.score)
  return {
    ...(allowed.includes('couName') ? { couName: row.couName } : {}),
    ...(allowed.includes('couCode') ? { couCode: row.couCode } : {}),
    ...(allowed.includes('couGeneral') ? { couGeneral: row.couGeneral ? 1 : 0 } : {}),
    ...(allowed.includes('couCredits') && couCredits != null ? { couCredits } : {}),
    // Left out until course credits are edited, so a score-only edit doesn't overwrite gained credit.
    ...(allowed.includes('couCredits') && row.creditsSynced ? { credits: gainedCreditsOf(choice, row) } : {}),
    ...(allowed.includes('score') && score != null ? { score } : {}),
  }
}

/** An open row with nothing typed in it is skipped on save, so untouched rows are never patched. */
export function hasRowChanged(
  choice: Api.AdministratorChoiceRow,
  row: ChoiceRowValues,
  allowed: readonly EditableField[]
): boolean {
  if (allowed.includes('couName') && row.couName !== choice.courseName) return true
  if (allowed.includes('couCode') && row.couCode !== choice.courseCode) return true
  if (allowed.includes('couGeneral') && row.couGeneral !== (choice.couGeneral === 1)) return true
  if (allowed.includes('couCredits') && numberOf(row.couCredits) !== choice.courseCredits) return true
  if (allowed.includes('couCredits') && gainedCreditsOf(choice, row) !== choice.credits) return true
  if (allowed.includes('score') && numberOf(row.score) !== choice.score) return true
  return false
}
